/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Alle filters" — every filter of the comparer in one sheet, for phones
 * (docs/plan-vergelijker-resultaten-2026-09-30.md §12.4 B5). The chip row of
 * the results bar stays the way to change ONE filter with one tap; this sheet
 * is for changing several at once and seeing what that does before it is done
 * (NN/g, "Mobile Faceted Search with a Tray"; plan §12.3 principle 7).
 *
 * How it behaves:
 *
 * - It edits a DRAFT. The draft starts from the address every time the sheet
 *   opens (closing unmounts the panel, which is the reset — the SignupModal
 *   idiom), the list behind it does not move while the visitor tries things,
 *   and the apply button says how many offers the draft would show. That count
 *   is the engine's own filter rule over the engine's own rows
 *   (src/lib/comparerFilter.ts), so it is the number the list will show.
 * - Apply hands the draft to the engine, which writes the address ONCE — one
 *   `filter_change` per changed filter and one `results_view`, instead of one
 *   of each per pill. Cancel, Escape and a tap on the veil discard it.
 * - At zero results the apply button is disabled and says why: a draft that
 *   empties the list is never applied.
 * - The volume field follows the hero's rules (digits only, clamped on commit,
 *   shown through `formatInteger`). Untouched, it follows the audience the way
 *   the address does: no volume parameter means the audience's own default,
 *   so switching to Zakelijk here shows 5.000 exactly as the list then will.
 * - The Voorwaarden group ends with the legend of the colours the rows paint
 *   their terms in (`offer_terms.legend`: green, orange, grey) — shown exactly
 *   while the group is. Its option labels are the comparer's own: the key
 *   FEATURE_LABEL_KEY names for each code (src/lib/providerFeatures.ts).
 * - No Score group: the Google-score filter left with the ratings (2026-10-14).
 *
 * Layout: `FILTER_SHEET_VIEWPORT_CLASS` on `ModalViewport`
 * (src/components/modalLayout.tsx) — on the bottom edge below `sm`, the
 * ordinary centred card from `sm`, and pinned to the visual viewport, so a
 * phone keyboard opened by the volume field shrinks the sheet (at most 85svh,
 * at most the visible screen) instead of covering it. The panel is a column:
 * the handle and title, the controls (the only part that scrolls), the two
 * buttons. Never in the bake: it mounts through ClientPortal only once the
 * visitor has asked for it.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, m, useIsPresent } from 'motion/react';
import { Check } from 'lucide-react';
import { ClientPortal } from '../ClientPortal';
import { FILTER_SHEET_VIEWPORT_CLASS, MODAL_BACKDROP_CLASS, ModalViewport } from '../modalLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { cn } from '../../lib/cn';
import { formatInteger } from '../../lib/formatters';
import { fillTemplate } from '../../lib/templateVars.js';
import { lockPageScroll } from '../../lib/scrollLock';
import {
  ALL_DURATIONS,
  durationOptions,
  isDurationSelected,
  toggleDurationSelection,
} from '../../lib/durationOptions';
import { parseVolume, type AudienceValue } from '../../lib/comparerParams';
import { VOLUME_MANUAL_MAX, VOLUME_MIN } from '../../lib/kwhEstimate';
import {
  applyComparerFilters,
  effectiveComparerFilters,
  type ComparerFilterGates,
  type ComparerFilters,
  type SmartChargingFilter,
  type TermsFilter,
} from '../../lib/comparerFilter';
import { FEATURE_LABEL_KEY } from '../../lib/providerFeatures';
import type { EREProvider } from '../../types';

/** Everything the sheet edits, in the address's own terms. */
export interface FilterSheetDraft {
  audience: AudienceValue;
  /**
   * The volume parameter as the address will carry it. null: none — the
   * audience's own default applies (src/lib/comparerParams.ts, parseVolume).
   */
  volumeParam: string | null;
  contractDuration: string;
  smartCharging: SmartChargingFilter;
  terms: TermsFilter;
}

/** The part of a draft that narrows the list (the volume only ranks). */
export const draftFilters = (draft: FilterSheetDraft): ComparerFilters => ({
  audience: draft.audience,
  contractDuration: draft.contractDuration,
  smartCharging: draft.smartCharging,
  terms: draft.terms,
});

interface FilterSheetProps {
  open: boolean;
  /** The address as it is when the sheet opens: the draft starts here every time. */
  initial: FilterSheetDraft;
  /** The whole data set: the live count and the looptijd options are computed over it. */
  providers: readonly EREProvider[];
  /** Which controls exist (the engine's own gates): a hidden filter is never offered. */
  gates: ComparerFilterGates;
  /** Apply: the draft and the number of offers it shows. */
  onApply: (draft: FilterSheetDraft, resultCount: number) => void;
  /** Cancel, Escape or the veil: the number of offers the discarded draft would have shown. */
  onCancel: (resultCount: number) => void;
  /** Where focus goes back to when the sheet closes — the button that opened it. */
  returnFocusRef: React.RefObject<HTMLElement | null>;
}

/** The audience values in the order the hero offers them: the two segments, then both. */
const AUDIENCE_ORDER: { value: AudienceValue; key: string }[] = [
  { value: 'Particulier', key: 'filters.particulier' },
  { value: 'Zakelijk', key: 'filters.zakelijk' },
  { value: 'Alle', key: 'filters.all' },
];

/**
 * The volume parameter a typed text commits to: digits only, clamped like the
 * hero, and 0 or nothing back to "no parameter" — the address reads a 0 as the
 * audience's default anyway, so the draft says so instead of writing `volume=0`.
 */
const committedVolumeParam = (text: string): string | null => {
  const parsed = Math.min(Math.max(parseInt(text) || 0, VOLUME_MIN), VOLUME_MANUAL_MAX);
  return parsed > 0 ? String(parsed) : null;
};

interface ChoiceOption {
  value: string;
  label: string;
}

/**
 * One filter as a group of native radios, drawn as chips: the legend names the
 * filter, arrow keys move inside the group, Tab moves to the next one. The
 * chosen chip carries a check as well as its colour (WCAG 1.4.1). An optional
 * `note` is one muted line under the chips, tied to the group with
 * aria-describedby so a screen reader reads it on entering the group.
 * `multiple` (the looptijd, 2026-10-16) draws the same chips over native
 * check boxes: `isSelected` says which are ticked, and a change hands the
 * option to `onChange` as a toggle the caller resolves.
 */
const ChoiceGroup: React.FC<{
  legend: string;
  name: string;
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  note?: string;
  multiple?: boolean;
  isSelected?: (value: string) => boolean;
}> = ({ legend, name, options, value, onChange, note, multiple = false, isSelected }) => (
  <fieldset className="min-w-0" aria-describedby={note ? `${name}-note` : undefined}>
    <legend className="field-label mb-2">{legend}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = multiple && isSelected ? isSelected(option.value) : option.value === value;
        return (
          <label
            key={option.value}
            className={cn(
              'relative inline-flex min-h-11 cursor-pointer select-none items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors',
              'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-slate-900',
              // Light: the literal emerald-800 hex on the tint — the emerald-300
              // token is #047857 there and measured 4.3:1 on this tint (the
              // results bar's pills had the same fix), and the emerald-800
              // token is remapped to a pastel.
              selected
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300 [html.light_&]:text-[#065f46]'
                : 'border-slate-700 bg-slate-800 text-slate-200 hover:border-slate-500'
            )}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {selected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {option.label}
          </label>
        );
      })}
    </div>
    {note && (
      <p id={`${name}-note`} className="mt-2 text-xs leading-relaxed text-fg-muted">
        {note}
      </p>
    )}
  </fieldset>
);

interface PanelProps extends Omit<FilterSheetProps, 'open' | 'returnFocusRef'> {
  dialogRef: React.RefObject<HTMLDivElement | null>;
  /** The draft's live count, for the Escape handler that lives outside the panel. */
  countRef: React.MutableRefObject<number>;
}

const FilterSheetPanel: React.FC<PanelProps> = ({
  initial,
  providers,
  gates,
  onApply,
  onCancel,
  dialogRef,
  countRef,
}) => {
  const { t, language } = useLanguage();
  // False while the exit animation plays: the panel is still on screen but is
  // no longer the visitor's — a second tap on "Toon" must not apply twice.
  const isPresent = useIsPresent();
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const volumeId = `${baseId}-volume`;

  const [draft, setDraft] = useState<FilterSheetDraft>(initial);
  // The volume field while it is being typed in; null = not being edited, so
  // it shows the draft's volume, formatted.
  const [volumeText, setVolumeText] = useState<string | null>(null);
  // A sheet rises from the bottom edge; from sm the same panel is the centred
  // card and fades in like the other modals. Read once, when the panel opens
  // (client-only — the sheet never renders on the server).
  const [asSheet] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia('(min-width: 640px)').matches
  );

  const count = useMemo(
    () =>
      applyComparerFilters(providers, effectiveComparerFilters(draftFilters(draft), gates)).length,
    [providers, gates, draft]
  );
  useEffect(() => {
    countRef.current = count;
  }, [count, countRef]);

  const update = <K extends keyof FilterSheetDraft>(key: K, value: FilterSheetDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /** The draft with whatever is still being typed in the volume field committed. */
  const committedDraft = (): FilterSheetDraft =>
    volumeText === null ? draft : { ...draft, volumeParam: committedVolumeParam(volumeText) };

  const commitVolume = () => {
    if (volumeText === null) return;
    setDraft(committedDraft());
    setVolumeText(null);
  };

  const shownVolume = parseVolume(draft.volumeParam, draft.audience);

  // The looptijd chips: the durations an offer actually has, plus the one in
  // the address when the sheet opened — the bar's list (src/lib/durationOptions.ts)
  // — kept steady while the draft changes, so no chip vanishes under a finger.
  const contractOptions = useMemo(
    () => durationOptions(providers, initial.contractDuration, t),
    [providers, initial.contractDuration, t]
  );
  const smartOptions: ChoiceOption[] = [
    { value: 'all', label: t('table.filter_smart_all') },
    { value: 'has', label: t('table.filter_smart_has_opt') },
    { value: 'none', label: t('table.filter_smart_none_opt') },
  ];
  const termsOptions: ChoiceOption[] = [
    { value: 'all', label: t('table.filter_terms_all') },
    ...gates.features.map((code) => ({ value: code, label: t(FEATURE_LABEL_KEY[code]) })),
  ];

  const applyLabel =
    count === 0
      ? t('filters.sheet_apply_none')
      : fillTemplate(t(count === 1 ? 'filters.sheet_apply_one' : 'filters.sheet_apply'), {
          count: formatInteger(count, language),
        });

  const apply = () => {
    if (!isPresent || count === 0) return;
    onApply(committedDraft(), count);
  };
  const cancel = () => {
    if (!isPresent) return;
    onCancel(count);
  };

  return (
    // While it animates out it is no longer the visitor's: inert (no clicks,
    // no focus) and out of the accessibility tree, so a sheet opened again
    // straight away is the only dialog there is.
    <ModalViewport
      className={FILTER_SHEET_VIEWPORT_CLASS}
      inert={!isPresent}
      aria-hidden={isPresent ? undefined : true}
    >
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={cancel}
        className={MODAL_BACKDROP_CLASS}
      />
      <m.div
        // Set, never cleared: a panel still animating out unmounts AFTER the
        // next one has mounted, and clearing on its way out would take the
        // focus trap away from the sheet that is open.
        ref={(el: HTMLDivElement | null) => {
          if (el) dialogRef.current = el;
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={asSheet ? { y: '100%' } : { opacity: 0, y: 24, scale: 0.97 }}
        animate={asSheet ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
        exit={
          asSheet
            ? { y: '100%', transition: { duration: 0.2, ease: 'easeIn' } }
            : { opacity: 0, y: 24, scale: 0.97, transition: { duration: 0.15 } }
        }
        transition={{ type: 'spring', damping: 34, stiffness: 340 }}
        className="relative flex max-h-[min(85svh,100%)] w-full flex-col overscroll-contain rounded-t-3xl border border-b-0 border-emerald-500/20 bg-slate-900 shadow-2xl shadow-emerald-900/30 outline-none sm:max-w-lg sm:rounded-3xl sm:border-b"
      >
        {/* The handle says "sheet" at a glance; it is a picture, not a control. */}
        <div className="shrink-0 touch-none border-b border-slate-700/60 px-5 pt-2.5 pb-3 sm:pt-4">
          <div
            aria-hidden="true"
            className="mx-auto mb-2.5 h-1.5 w-10 rounded-full bg-slate-600 sm:hidden"
          />
          <h2 id={titleId} className="text-lg font-extrabold text-white">
            {t('filters.sheet_open')}
          </h2>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-4">
          <div>
            <label htmlFor={volumeId} className="field-label mb-2 block">
              {t('filters.volume')}
            </label>
            <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-3 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500">
              {/* text-base: iOS zooms the page into any field under 16 px. */}
              <input
                id={volumeId}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={volumeText ?? formatInteger(shownVolume, language)}
                onChange={(e) => setVolumeText(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={commitVolume}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                className="min-w-0 flex-1 bg-transparent text-base font-bold tabular-nums text-white outline-none"
              />
              <span className="text-sm font-bold text-fg-muted">{t('table.kwh')}</span>
            </div>
          </div>

          <fieldset className="min-w-0">
            <legend className="field-label mb-2">{t('table.audience')}</legend>
            <div className="grid grid-cols-3 gap-1 rounded-xl border border-slate-700 bg-slate-950 p-1">
              {AUDIENCE_ORDER.map((option) => {
                const selected = option.value === draft.audience;
                return (
                  <label
                    key={option.value}
                    className={cn(
                      'relative flex min-h-11 cursor-pointer select-none items-center justify-center rounded-lg px-2 text-center text-sm font-bold transition-colors',
                      'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus-ring',
                      selected
                        ? 'bg-emerald-700 text-white shadow-sm'
                        : 'text-slate-300 hover:bg-slate-800'
                    )}
                  >
                    <input
                      type="radio"
                      name={`${baseId}-audience`}
                      value={option.value}
                      checked={selected}
                      onChange={() => update('audience', option.value)}
                      className="sr-only"
                    />
                    {t(option.key)}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <ChoiceGroup
            legend={t('table.duration')}
            name={`${baseId}-duration`}
            options={contractOptions}
            value={draft.contractDuration}
            multiple
            isSelected={(option) => isDurationSelected(draft.contractDuration, option)}
            onChange={(option) =>
              update(
                'contractDuration',
                toggleDurationSelection(
                  draft.contractDuration,
                  option,
                  contractOptions.filter((o) => o.value !== ALL_DURATIONS).map((o) => o.value)
                )
              )
            }
          />
          <ChoiceGroup
            legend={t('table.col_smart_charger')}
            name={`${baseId}-smart`}
            options={smartOptions}
            value={draft.smartCharging}
            onChange={(value) => update('smartCharging', value as SmartChargingFilter)}
          />
          {gates.features.length > 0 && (
            <ChoiceGroup
              legend={t('table.col_terms')}
              name={`${baseId}-terms`}
              options={termsOptions}
              value={draft.terms}
              onChange={(value) => update('terms', value as TermsFilter)}
              note={t('offer_terms.legend')}
            />
          )}
        </div>

        <div className="flex shrink-0 touch-none gap-3 border-t border-slate-700/60 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
          <button
            type="button"
            onClick={cancel}
            className="h-11 shrink-0 rounded-xl border border-slate-700 px-4 text-sm font-bold text-slate-200 transition-colors hover:bg-slate-800 focus-ring"
          >
            {t('filters.sheet_cancel')}
          </button>
          {/* Hover: the literal emerald-800 hex — the token is a pastel in light
            mode, and white on it would vanish. */}
          <button
            type="button"
            onClick={apply}
            disabled={count === 0}
            className="h-11 min-w-0 flex-1 rounded-xl bg-emerald-700 px-4 text-sm font-black text-white transition-colors hover:bg-[#065f46] focus-ring disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-200"
          >
            {applyLabel}
          </button>
        </div>
        {/* The count, said again for a screen reader each time a choice changes it. */}
        <p role="status" className="sr-only">
          {applyLabel}
        </p>
      </m.div>
    </ModalViewport>
  );
};

/**
 * The sheet. Mount it always (closed, it renders nothing — not even on the
 * server, where ClientPortal renders null); `open` decides.
 */
export const FilterSheet: React.FC<FilterSheetProps> = ({ open, returnFocusRef, ...panel }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const countRef = useRef(0);
  // Every opening is a new panel, so a new draft: re-adding the SAME key while
  // the previous panel is still animating out would revive that panel — and
  // the draft the visitor just discarded with it.
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((s) => s + 1);
  }
  // The signup modal's idiom: the page locked, Tab kept inside, Escape
  // discards, and focus back on the opener the moment the sheet closes — not
  // after its exit animation. The lock is released before the focus goes back.
  useEffect(() => (open ? lockPageScroll() : undefined), [open]);
  // The dialog itself takes the focus, not its first control: that is the
  // volume field, and focusing it would open a phone's keyboard over the
  // sheet. The panel mounts in the same commit as `open`; the frame is a
  // margin for a portal that mounts a render later. The dialog is not one of
  // the stops, so from there Tab goes to the first control and Shift+Tab to
  // the last ('off-stop').
  useFocusTrap(dialogRef, open, () => panel.onCancel(countRef.current), {
    scope: 'document',
    returnFocusRef,
    initialFocus: 'container',
    recapture: 'off-stop',
  });

  return (
    <ClientPortal>
      <AnimatePresence>
        {open && (
          <FilterSheetPanel
            key={`filter-sheet-${session}`}
            {...panel}
            dialogRef={dialogRef}
            countRef={countRef}
          />
        )}
      </AnimatePresence>
    </ClientPortal>
  );
};
